// The export buttons' shared download: one CSV export with the cap toast, and
// a button state that ignores a second click while a file is on its way.

import { toast } from "@pej/kore/components";
import { useRef, useState } from "react";
import { downloadResponsesCsv, type ResponseExportRq } from "@/api/responses";
import { toastMutationError } from "@/components/error-toast";

/** Downloads one export and says when the server capped it. Behind both
 *  exports: Results' Export CSV (the segment) and Responses' Export
 *  (everything the table shows). */
async function exportResponses(surveyId: string, rq: ResponseExportRq): Promise<void> {
    try {
        const { truncated } = await downloadResponsesCsv(surveyId, rq);
        if (truncated) {
            toast.warning("Export capped at 100,000 rows", {
                description: "Narrow the filters to export the rest.",
            });
        }
    } catch (err) {
        toastMutationError("Export failed", err);
    }
}

/** An export button's state: `exporting` while a file is on its way, and a
 *  `run` that ignores a click made in the meantime, so an impatient second
 *  click on a large export doesn't download it twice. */
export function useExport(surveyId: string) {
    const [exporting, setExporting] = useState(false);
    // The flag itself, for a second click in the same render as the first.
    const inFlight = useRef(false);
    async function run(rq: ResponseExportRq) {
        if (inFlight.current) return;
        inFlight.current = true;
        setExporting(true);
        try {
            await exportResponses(surveyId, rq);
        } finally {
            inFlight.current = false;
            setExporting(false);
        }
    }
    return { exporting, run };
}
